import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Calendar,
  CheckCircle,
  Clock,
  LogIn,
  MessageCircle,
  Sparkles,
  Users,
} from 'lucide-react';
import { PlanDisplay } from './PlanCard';
import TherapistPlanCard, { PlanSecondaryAction } from './TherapistPlanCard';
import { ParticipantIntent } from './PlansIntentBanner';
import {
  readPendingConstellationAppointment,
  type PendingConstellationAppointment,
} from '../../utils/pendingConstellationAppointment';

const WHATSAPP_AVISOS_GRUPO =
  'https://chat.whatsapp.com/DsJxwW1i4IJ6pernEwmtQC';

interface JourneyStep {
  title: string;
  text: string;
  href?: string;
  hrefLabel?: string;
  external?: boolean;
}

interface ParticipantPlansLayoutProps {
  plans: PlanDisplay[];
  isLoading: boolean;
  processingPlan: string | null;
  intent: ParticipantIntent | null;
  onSelect: (id: string, quantity?: number) => void;
  /** true quando veio do agendamento sem crédito — focar no pagamento */
  paymentStep?: boolean;
}

function formatPendingSummary(pending: PendingConstellationAppointment): string {
  try {
    const dt = new Date(pending.appointmentDateTime);
    const date = dt.toLocaleDateString('pt-BR');
    const time = dt.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    const name = pending.therapistName || 'constelador escolhido';
    return `${name} · ${date} às ${time}`;
  } catch {
    return pending.therapistName || 'Sessão já escolhida';
  }
}

const participateBenefits = [
  'Ampliação da consciência sobre padrões repetitivos.',
  'Maior compreensão das dinâmicas familiares.',
  'Mais leveza emocional.',
  'Melhora nos relacionamentos.',
  'Sensação de pertencimento e conexão.',
  'Liberação de bloqueios inconscientes.',
  'Desenvolvimento pessoal.',
  'Novas perspectivas para desafios antigos.',
];

const constellateTopics = [
  'Relacionamentos amorosos que não dão certo.',
  'Separações difíceis ou conflitos conjugais.',
  'Dificuldades financeiras e bloqueios para prosperar.',
  'Conflitos entre pais e filhos.',
  'Problemas familiares recorrentes.',
  'Baixa autoestima e insegurança.',
  'Sentimento de não pertencimento.',
  'Dificuldade para encontrar propósito ou direção na vida.',
  'Processos de luto e perdas importantes.',
  'Dificuldades profissionais, estagnação ou falta de reconhecimento.',
];

function Prose({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={`space-y-4 text-sm leading-relaxed text-[#496566] sm:text-base ${className}`}>
      {children}
    </div>
  );
}

function ParticipateWhySection() {
  return (
    <div className="space-y-5">
      <Prose>
        <p>
          Muitas pessoas chegam à Constelação Familiar porque estão enfrentando um problema específico.
          No entanto, o que descobrem é muito maior do que a solução de uma questão: encontram novas formas
          de enxergar a si mesmas, seus relacionamentos e sua vida.
        </p>
        <p>
          Participar de uma constelação, mesmo como representante ou observador, é uma experiência profunda.
          Isso acontece porque os temas apresentados por outras pessoas frequentemente despertam algo dentro
          de nós. Chamamos isso de <strong className="font-semibold text-[#3A5859]">ressonância</strong>.
          Muitas vezes, ao assistir à constelação de alguém, recebemos insights valiosos sobre situações que
          estamos vivendo e nem imaginávamos que estavam relacionadas.
        </p>
      </Prose>

      <blockquote className="rounded-2xl border-l-4 border-[#3A5859] bg-[#F3F6F1]/80 px-5 py-4 italic text-[#3A5859]">
        &ldquo;Eu vim apenas para assistir e saí com respostas que procurava há anos.&rdquo;
      </blockquote>

      <Prose>
        <p>
          Cada encontro é único. A cada participação, novos entendimentos surgem, novas camadas são reveladas
          e novas possibilidades aparecem. Afinal, estamos em constante transformação e aquilo que não
          conseguimos compreender hoje pode se tornar claro amanhã.
        </p>
        <p>Além dos insights, a participação frequente proporciona benefícios importantes:</p>
      </Prose>

      <ul className="grid gap-2 sm:grid-cols-2">
        {participateBenefits.map((benefit) => (
          <li key={benefit} className="flex items-start gap-2.5 text-sm text-[#496566] sm:text-base">
            <CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-[#3A5859]" aria-hidden />
            <span>{benefit}</span>
          </li>
        ))}
      </ul>

      <Prose>
        <p>
          Por isso, muitas pessoas fazem da Constelação uma prática contínua de autoconhecimento e crescimento.
          Cada encontro abre portas para uma vida mais consciente, leve e alinhada com quem você realmente é.
        </p>
      </Prose>
    </div>
  );
}

function ConstellateWhySection() {
  return (
    <div className="space-y-5">
      <Prose>
        <p>
          Existem momentos em que sentimos que estamos dando o nosso melhor, mas algo parece nos impedir de
          avançar. Problemas se repetem, relacionamentos se desgastam, decisões se tornam difíceis e, muitas
          vezes, não conseguimos entender a verdadeira origem dessas situações.
        </p>
        <p>
          A Constelação Familiar permite olhar para além do problema aparente e acessar dinâmicas ocultas que
          podem estar influenciando sua vida de forma inconsciente.
        </p>
        <p>
          Durante a constelação, seu tema é representado em um campo sistêmico que revela conexões,
          emaranhamentos e padrões que normalmente não conseguimos enxergar sozinhos. A partir dessa nova
          visão, torna-se possível encontrar caminhos mais saudáveis e soluções que antes pareciam
          impossíveis.
        </p>
        <p>A Constelação pode ajudar em questões como:</p>
      </Prose>

      <ol className="space-y-2 pl-1">
        {constellateTopics.map((topic, index) => (
          <li key={topic} className="flex gap-3 text-sm text-[#496566] sm:text-base">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#A56096]/15 text-xs font-bold text-[#A56096]">
              {index + 1}
            </span>
            <span className="pt-0.5">{topic}</span>
          </li>
        ))}
      </ol>

      <Prose>
        <p>
          Mas esses são apenas alguns exemplos. Qualquer situação que esteja gerando sofrimento, conflito,
          repetição ou bloqueio pode ser levada para uma constelação.
        </p>
      </Prose>

      <div className="space-y-4 rounded-2xl border border-[#A56096]/20 bg-gradient-to-br from-[#A56096]/5 to-[#F3F6F1] p-5 sm:p-6">
        <Prose>
          <p>
            Se você sente que existe algo em sua vida pedindo solução, clareza ou movimento, a Constelação
            Familiar pode ser o primeiro passo para enxergar o que ainda está oculto e abrir espaço para uma
            nova realidade.
          </p>
          <p>
            Para muitas pessoas, a experiência se torna um verdadeiro divisor de águas. Não porque a
            constelação &ldquo;muda a vida&rdquo; por mágica, mas porque muda a forma como a pessoa enxerga sua
            história, suas escolhas e suas possibilidades.
          </p>
          <p>
            Quando aquilo que estava oculto se torna visível, surge a oportunidade de fazer movimentos
            diferentes. E são esses novos movimentos que podem transformar relacionamentos, decisões,
            resultados e até mesmo a maneira como você se sente consigo mesma.
          </p>
          <p>
            Talvez você já tenha tentado conversar, refletir, ler livros e buscar respostas. A Constelação
            oferece algo complementar: uma experiência vivencial que permite acessar informações que muitas
            vezes estão além do pensamento racional.
          </p>
          <p>
            Se existe uma situação que continua se repetindo, causando dor ou impedindo você de avançar,
            talvez seja o momento de olhar para ela sob uma nova perspectiva.
          </p>
          <p className="font-medium text-[#3A5859]">
            Uma única constelação pode trazer compreensões profundas. E uma nova compreensão pode mudar
            completamente o rumo da sua história.
          </p>
        </Prose>
      </div>
    </div>
  );
}

const stepsParticipate: JourneyStep[] = [
  {
    title: 'Entre no grupo de avisos',
    text: 'Clique no link do grupo do WhatsApp para receber os avisos dos próximos encontros de constelação em grupo.',
    href: WHATSAPP_AVISOS_GRUPO,
    hrefLabel: 'Entrar no grupo do WhatsApp',
    external: true,
  },
  {
    title: 'Acompanhe os próximos encontros',
    text: 'Pelo grupo, você fica sabendo quando haverá sessões abertas para observar ou participar como representante.',
  },
  {
    title: 'Participe no horário',
    text: 'No dia e horário informado, acesse o link da sessão enviado no grupo ou disponível em Salas abertas.',
  },
  {
    title: 'Solicite entrada na sala',
    text: 'Informe seu nome, libere câmera e microfone e aguarde o constelador aprovar sua entrada.',
  },
  {
    title: 'Represente no campo (quando convidado)',
    text: 'Se for promovido a representante, escolha seu avatar e movimente-se no cenário virtual.',
  },
];

const stepsConstellate: JourneyStep[] = [
  {
    title: 'Escolha o seu constelador',
    text: 'Navegue pela lista de profissionais homologados e escolha quem mais combina com você.',
    href: '/terapeutas',
    hrefLabel: 'Escolher constelador',
  },
  {
    title: 'Escolha dia e horário',
    text: 'Na página do constelador, veja a disponibilidade real e selecione o melhor horário para você.',
  },
  {
    title: 'Efetue o pagamento para confirmar',
    text: 'Depois de escolher o profissional, o dia e o horário, conclua o pagamento para garantir sua sessão.',
  },
  {
    title: 'Acesse no dia da sessão',
    text: 'Entre pelo link enviado, pelo seu Painel ou pelo botão da sessão agendada.',
  },
  {
    title: 'Vivencie sua constelação',
    text: 'Na videochamada integrada, com avatares no campo virtual, traga seu tema com apoio profissional.',
  },
];

const faqItems = [
  {
    question: 'Constelação online funciona?',
    answer:
      'Sim. O campo morfogenético atua no virtual com a mesma profundidade do presencial. A plataforma garante posicionamento, representação e verbalização dos representantes.',
  },
  {
    question: 'Qual a diferença entre participar e constelar?',
    answer:
      'Participar é vivenciar sessões de outras pessoas como observador ou representante. Constelar é trazer o seu próprio tema e fazer a sua sessão completa com um constelador.',
  },
  {
    question: 'Preciso instalar algum programa?',
    answer:
      'Não. Tudo funciona no navegador do computador, tablet ou celular. Basta uma boa conexão de internet.',
  },
];

function planKind(plan: PlanDisplay): 'participar' | 'constelacao' | 'geral' {
  const blob = `${plan.name} ${plan.sessionLabel ?? ''} ${plan.description ?? ''}`.toLowerCase();
  if (/represent|particip|clube|observador/.test(blob)) return 'participar';
  if (/constel|sessão completa|sessao completa|sua constela/.test(blob)) return 'constelacao';
  return 'geral';
}

function splitPlans(plans: PlanDisplay[]) {
  const participar: PlanDisplay[] = [];
  const constelacao: PlanDisplay[] = [];
  const geral: PlanDisplay[] = [];

  for (const plan of plans) {
    const kind = planKind(plan);
    if (kind === 'participar') participar.push(plan);
    else if (kind === 'constelacao') constelacao.push(plan);
    else geral.push(plan);
  }

  return { participar, constelacao, geral };
}

function StepTimeline({
  steps,
  accentClass,
  numberClass,
}: {
  steps: JourneyStep[];
  accentClass: string;
  numberClass: string;
}) {
  return (
    <ol className="space-y-0">
      {steps.map((step, index) => (
        <li key={step.title} className="relative flex gap-4 pb-8 last:pb-0">
          {index < steps.length - 1 && (
            <span
              className={`absolute left-[1.15rem] top-10 h-[calc(100%-2rem)] w-px ${accentClass}`}
              aria-hidden
            />
          )}
          <span
            className={`relative z-10 flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white ${numberClass}`}
          >
            {index + 1}
          </span>
          <div className="pt-0.5">
            <h4 className="font-bold text-[#3A5859]">{step.title}</h4>
            <p className="mt-1 text-sm leading-relaxed text-[#496566]">{step.text}</p>
            {step.href && step.hrefLabel && (
              step.external ? (
                <a
                  href={step.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-3 inline-flex items-center gap-2 rounded-full bg-[#25D366] px-4 py-2 text-xs font-semibold text-white transition hover:bg-[#20BA5A]"
                >
                  <MessageCircle className="h-4 w-4" aria-hidden />
                  {step.hrefLabel}
                </a>
              ) : (
                <Link
                  to={step.href}
                  className="mt-3 inline-flex items-center gap-2 rounded-full bg-[#A56096] px-4 py-2 text-xs font-semibold text-white transition hover:bg-[#945586]"
                >
                  {step.hrefLabel}
                  <ArrowRight className="h-3.5 w-3.5" aria-hidden />
                </Link>
              )
            )}
          </div>
        </li>
      ))}
    </ol>
  );
}

function ParticipationPromoBanner({ className = '' }: { className?: string }) {
  return (
    <div
      className={`rounded-[1.75rem] border-2 border-[#7ED99C] bg-gradient-to-br from-[#7ED99C]/25 via-[#E8F5EA] to-[#F3F6F1] p-6 text-center shadow-[0_12px_32px_rgba(45,90,61,0.12)] md:p-8 lg:text-left ${className}`}
    >
      <p className="text-sm font-extrabold uppercase tracking-[0.18em] text-[#2d5a3d]">
        Promoção · Por tempo limitado
      </p>
      <p className="mx-auto mt-4 max-w-3xl text-lg font-bold leading-relaxed text-[#3A5859] sm:text-xl lg:mx-0">
        <span className="text-[#2d5a3d]">Participação gratuita:</span>{' '}
        <a
          href={WHATSAPP_AVISOS_GRUPO}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[#3A5859] underline decoration-[#7ED99C] decoration-2 underline-offset-4 transition hover:text-[#A56096]"
        >
          entre no grupo do WhatsApp
        </a>{' '}
        <span className="font-semibold text-[#496566]">para receber os avisos dos próximos encontros.</span>
      </p>
    </div>
  );
}

function PlansGrid({
  plans,
  isLoading,
  processingPlan,
  onSelect,
  emptyMessage,
  secondaryAction,
  fillColumn = false,
  stretchCards = false,
}: {
  plans: PlanDisplay[];
  isLoading: boolean;
  processingPlan: string | null;
  onSelect: (id: string, quantity?: number) => void;
  emptyMessage: string;
  secondaryAction?: PlanSecondaryAction;
  fillColumn?: boolean;
  stretchCards?: boolean;
}) {
  const planCols =
    plans.length === 1
      ? fillColumn
        ? 'w-full'
        : 'max-w-md mx-auto'
      : plans.length === 2
        ? 'md:grid-cols-2 max-w-4xl mx-auto'
        : 'md:grid-cols-2 lg:grid-cols-3 max-w-6xl mx-auto';

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div
          className="inline-block h-10 w-10 animate-spin rounded-full border-4 border-solid border-[#A56096] border-r-transparent"
          role="status"
          aria-label="Carregando planos"
        />
        <p className="mt-4 font-medium text-[#496566]">Carregando planos...</p>
      </div>
    );
  }

  if (plans.length === 0) {
    return (
      <div className="rounded-[1.5rem] border border-dashed border-[#E8EFEB] bg-white py-12 text-center">
        <p className="text-[#496566]">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div
      className={`grid grid-cols-1 gap-6 md:gap-8 ${planCols} ${stretchCards ? 'min-h-0 flex-1' : ''}`}
    >
      {plans.map((plan) => (
        <div key={plan.id} className={`flex flex-col ${stretchCards ? 'h-full min-h-0' : 'h-full'}`}>
          <TherapistPlanCard
            plan={plan}
            onSelect={onSelect}
            isLoading={processingPlan === plan.id}
            secondaryAction={secondaryAction}
          />
        </div>
      ))}
    </div>
  );
}

function JourneyCard({
  id,
  eyebrow,
  title,
  intro,
  whyHeading,
  whyContent,
  steps,
  accent,
  cta,
  promo,
}: {
  id: string;
  eyebrow: string;
  title: string;
  intro: string;
  whyHeading: string;
  whyContent: React.ReactNode;
  steps: JourneyStep[];
  accent: 'participar' | 'constelar';
  cta: { label: string; to?: string; href?: string };
  promo?: { label: string; detail: string };
}) {
  const isParticipar = accent === 'participar';
  const headerClass = isParticipar
    ? 'from-[#3A5859] to-[#496566]'
    : 'from-[#A56096] to-[#8B4A7D]';
  const numberClass = isParticipar ? 'bg-[#3A5859]' : 'bg-[#A56096]';
  const lineClass = isParticipar ? 'bg-[#3A5859]/20' : 'bg-[#A56096]/20';

  return (
    <section
      id={id}
      className="scroll-mt-28 overflow-hidden rounded-[1.75rem] border border-[#E8EFEB] bg-white shadow-[0_18px_40px_rgba(58,88,89,0.06)]"
      aria-labelledby={`${id}-heading`}
    >
      <div className={`bg-gradient-to-br ${headerClass} px-6 py-8 text-white md:px-8 md:py-10`}>
        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-white/75">{eyebrow}</p>
        <h2 id={`${id}-heading`} className="mt-2 text-2xl font-bold md:text-3xl">
          {title}
        </h2>
        <p className="mt-3 max-w-2xl text-sm leading-relaxed text-white/90 md:text-base">{intro}</p>
        <div className="mt-5 flex flex-col items-start gap-4">
          {promo && (
            <div className="flex max-w-xl flex-col gap-1 rounded-2xl border border-white/25 bg-white/15 px-4 py-3 backdrop-blur-sm">
              <span className="text-xs font-bold uppercase tracking-[0.14em] text-white">{promo.label}</span>
              <span className="text-sm text-white/95">{promo.detail}</span>
            </div>
          )}
          {cta.href ? (
            <a
              href={cta.href}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-[#3A5859] shadow-md transition hover:bg-white/95"
            >
              {cta.label}
              <ArrowRight className="h-4 w-4" aria-hidden />
            </a>
          ) : (
            <Link
              to={cta.to || '/'}
              className="inline-flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-[#3A5859] shadow-md transition hover:bg-white/95"
            >
              {cta.label}
              <ArrowRight className="h-4 w-4" aria-hidden />
            </Link>
          )}
        </div>
      </div>

      <div className="p-6 md:p-8">
        <div className="mb-10">
          <h3 className="mb-5 text-xl font-bold leading-snug text-[#3A5859] sm:text-2xl">
            {whyHeading}
          </h3>
          {whyContent}
        </div>

        <div className="rounded-[1.25rem] border border-[#E8EFEB] bg-[#F3F6F1]/40 p-5 md:p-6">
          <h3 className="mb-4 flex items-center gap-2 text-lg font-bold text-[#3A5859]">
            <LogIn className={`h-5 w-5 ${isParticipar ? 'text-[#3A5859]' : 'text-[#A56096]'}`} aria-hidden />
            Passo a passo na plataforma
          </h3>
          <StepTimeline steps={steps} accentClass={lineClass} numberClass={numberClass} />
        </div>
      </div>
    </section>
  );
}

function ParticipantHero({
  intent,
  pendingAppointment,
}: {
  intent: ParticipantIntent | null;
  pendingAppointment: PendingConstellationAppointment | null;
}) {
  if (intent === 'participar') {
    return (
      <div className="rounded-[1.75rem] border border-[#E8EFEB] bg-gradient-to-br from-[#3A5859] to-[#496566] p-8 text-white shadow-xl md:p-10">
        <p className="mb-2 text-sm font-semibold uppercase tracking-[0.16em] text-white/75">Quero participar</p>
        <span className="mb-3 inline-flex rounded-full bg-[#7ED99C] px-3 py-1 text-xs font-bold uppercase tracking-wide text-[#2d5a3d]">
          Por tempo limitado
        </span>
        <h1 className="text-3xl font-bold leading-tight md:text-4xl">Participe de uma constelação</h1>
        <p className="mt-4 max-w-2xl text-lg leading-relaxed text-white/90">
          Por enquanto, sem custo: entre no grupo do WhatsApp e receba os avisos dos próximos encontros.
        </p>
        <a
          href={WHATSAPP_AVISOS_GRUPO}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-6 inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-semibold text-[#3A5859] shadow-lg transition hover:bg-white/95"
        >
          <Users className="h-5 w-5" aria-hidden />
          Entrar no grupo de avisos
        </a>
      </div>
    );
  }

  if (intent === 'constelacao' && pendingAppointment) {
    return (
      <div className="rounded-[1.75rem] border border-[#E8EFEB] bg-gradient-to-br from-[#A56096] to-[#8B4A7D] p-8 text-white shadow-xl md:p-10">
        <p className="mb-2 text-sm font-semibold uppercase tracking-[0.16em] text-white/80">
          Quase lá — confirme sua sessão
        </p>
        <h1 className="text-3xl font-bold leading-tight md:text-4xl">Finalize o pagamento</h1>
        <p className="mt-4 max-w-2xl text-lg leading-relaxed text-white/95">
          Você já escolheu constelador, dia e horário. Escolha o plano abaixo para confirmar — sem precisar
          selecionar o profissional de novo.
        </p>
        <div className="mt-5 inline-flex max-w-full flex-col gap-2 rounded-2xl bg-white/15 px-4 py-3 text-sm text-white ring-1 ring-white/20 sm:flex-row sm:items-center sm:gap-4">
          <span className="inline-flex items-center gap-2 font-semibold">
            <Calendar className="h-4 w-4 shrink-0" aria-hidden />
            {formatPendingSummary(pendingAppointment)}
          </span>
          <span className="inline-flex items-center gap-2 text-white/85">
            <Clock className="h-4 w-4 shrink-0" aria-hidden />
            Horário reservado até o pagamento
          </span>
        </div>
        <a
          href="#planos"
          className="mt-6 inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-semibold text-[#A56096] shadow-lg transition hover:bg-white/95"
        >
          Ver planos e pagar
          <ArrowRight className="h-4 w-4" aria-hidden />
        </a>
      </div>
    );
  }

  if (intent === 'constelacao') {
    return (
      <div className="rounded-[1.75rem] border border-[#E8EFEB] bg-gradient-to-br from-[#A56096] to-[#8B4A7D] p-8 text-white shadow-xl md:p-10">
        <p className="mb-2 text-sm font-semibold uppercase tracking-[0.16em] text-white/80">
          Quero fazer minha constelação
        </p>
        <h1 className="text-3xl font-bold leading-tight md:text-4xl">Sua Constelação Familiar</h1>
        <p className="mt-4 max-w-2xl text-lg leading-relaxed text-white/95">
          Escolha um constelador homologado, veja a disponibilidade e confirme com o pagamento.
        </p>
        <Link
          to="/terapeutas"
          className="mt-6 inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-semibold text-[#A56096] shadow-lg transition hover:bg-white/95"
        >
          <Sparkles className="h-5 w-5" aria-hidden />
          Escolher constelador
        </Link>
      </div>
    );
  }

  return (
    <div className="relative overflow-hidden rounded-[1.75rem] border border-[#E8EFEB] bg-white p-8 shadow-[0_18px_40px_rgba(58,88,89,0.08)] md:p-10">
      <div className="pointer-events-none absolute -right-10 -top-10 h-48 w-48 rounded-full bg-[#A56096]/10 blur-3xl" aria-hidden />
      <div className="pointer-events-none absolute -bottom-10 -left-10 h-40 w-40 rounded-full bg-[#3A5859]/10 blur-3xl" aria-hidden />

      <div className="relative">
        <p className="mb-3 text-sm font-semibold uppercase tracking-[0.16em] text-[#A56096]">
          Para quem quer vivenciar
        </p>
        <h1 className="text-3xl font-bold leading-tight text-[#3A5859] md:text-4xl lg:text-5xl">
          Garanta o seu acesso para{' '}
          <span className="text-[#A56096]">Constelar</span> ou{' '}
          <span className="text-[#A56096]">Participar</span>
        </h1>
        <p className="mt-5 max-w-3xl text-lg leading-relaxed text-[#496566]">
          Escolha o caminho que faz sentido para você. Abaixo, explicamos por que participar ou
          constelar e o passo a passo dentro da plataforma.
        </p>

        <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
          <a
            href="#participar"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-[#3A5859] px-6 py-3 text-sm font-semibold text-white shadow-lg transition hover:bg-[#496566]"
          >
            <Users className="h-5 w-5" aria-hidden />
            Por que participar
          </a>
          <a
            href="#constelar"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-[#A56096] px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-[#A56096]/20 transition hover:bg-[#945586]"
          >
            <Sparkles className="h-5 w-5" aria-hidden />
            Por que constelar
          </a>
          <a
            href="#planos"
            className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-[#3A5859]/20 px-6 py-3 text-sm font-semibold text-[#3A5859] transition hover:border-[#3A5859]/40 hover:bg-[#F3F6F1]"
          >
            Ver planos
            <ArrowRight className="h-4 w-4" aria-hidden />
          </a>
        </div>
      </div>
    </div>
  );
}

const ParticipantPlansLayout: React.FC<ParticipantPlansLayoutProps> = ({
  plans,
  isLoading,
  processingPlan,
  intent,
  onSelect,
  paymentStep = false,
}) => {
  const pendingAppointment = React.useMemo(() => {
    if (paymentStep || intent === 'constelacao') {
      return readPendingConstellationAppointment();
    }
    return null;
  }, [paymentStep, intent]);

  const hasPendingSchedule = Boolean(pendingAppointment);

  React.useEffect(() => {
    if (!hasPendingSchedule && !paymentStep) return;
    const timer = window.setTimeout(() => {
      document.getElementById('planos')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 120);
    return () => window.clearTimeout(timer);
  }, [hasPendingSchedule, paymentStep]);

  const { participar, constelacao, geral } = splitPlans(plans);

  const participationPlans =
    intent === 'constelacao' ? [] : [...participar, ...(intent === 'participar' ? [] : geral)];
  const constellationPlans =
    intent === 'participar' ? [] : [...constelacao];

  const showParticiparJourney = intent !== 'constelacao';
  const showConstelarJourney = intent !== 'participar';

  const showConstellationOffers = intent !== 'participar' && (constellationPlans.length > 0 || isLoading);
  const showParticipationOffers = intent !== 'constelacao' && (participationPlans.length > 0 || isLoading);
  const offersSideBySide = showConstellationOffers && showParticipationOffers;

  const constellationOffers = showConstellationOffers ? (
    <div className={offersSideBySide ? 'flex h-full min-h-0 flex-col' : undefined}>
      <h3
        className={`mb-6 text-lg font-bold text-[#3A5859] sm:text-xl ${
          offersSideBySide ? 'text-center lg:text-left' : 'text-center lg:text-left'
        }`}
      >
        Para <span className="text-[#A56096]">constelar</span>
      </h3>
      <div className={offersSideBySide ? 'flex flex-1 flex-col min-h-0' : undefined}>
        <PlansGrid
          plans={constellationPlans}
          isLoading={isLoading}
          processingPlan={processingPlan}
          onSelect={onSelect}
          emptyMessage="Nenhum plano de constelação disponível no momento."
          secondaryAction={
            hasPendingSchedule
              ? undefined
              : {
                  hint: 'Escolher o seu constelador e agendar',
                  label: 'Escolher Constelador',
                  to: '/terapeutas',
                }
          }
          fillColumn={offersSideBySide}
          stretchCards={offersSideBySide}
        />
      </div>
    </div>
  ) : null;

  const participationOffers = showParticipationOffers ? (
    <div className={offersSideBySide ? 'flex h-full min-h-0 flex-col' : undefined}>
      {!offersSideBySide && <ParticipationPromoBanner className="mb-8" />}
      {offersSideBySide && <ParticipationPromoBanner className="mb-8 lg:hidden" />}
      <h3
        className={`mb-6 text-lg font-bold text-[#3A5859] sm:text-xl ${
          offersSideBySide ? 'text-center lg:text-left' : 'text-center'
        }`}
      >
        Para <span className="text-[#3A5859]">participar</span>
      </h3>
      <div className={offersSideBySide ? 'flex flex-1 flex-col min-h-0' : undefined}>
        <PlansGrid
          plans={participationPlans}
          isLoading={isLoading}
          processingPlan={processingPlan}
          onSelect={onSelect}
          emptyMessage="Nenhum plano de participação disponível no momento."
          fillColumn={offersSideBySide}
          stretchCards={offersSideBySide}
          secondaryAction={{
            hint: 'Ou receba avisos gratuitos dos próximos encontros',
            label: 'Entrar no grupo de avisos',
            href: WHATSAPP_AVISOS_GRUPO,
            external: true,
          }}
        />
      </div>
    </div>
  ) : null;

  return (
    <>
      <div className="mx-auto max-w-6xl px-1 sm:px-2">
        <Link
          to="/planos"
          className="plans-animate-in mb-8 inline-flex items-center gap-2 rounded-full border border-[#E8EFEB] bg-white/90 px-4 py-2 text-sm font-medium text-[#3A5859] shadow-sm backdrop-blur-sm transition hover:border-[#A56096]/30 hover:shadow-md"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden />
          Voltar para escolha de caminho
        </Link>

        <div className="plans-animate-in plans-animate-delay-1 mb-12">
          <ParticipantHero intent={intent} pendingAppointment={pendingAppointment} />
        </div>

        {!hasPendingSchedule && (
        <div className="space-y-10">
          {showParticiparJourney && (
            <JourneyCard
              id="participar"
              eyebrow="Caminho 1"
              title="Participar de uma sessão"
              intro="Entre no grupo de avisos no WhatsApp e receba as datas dos próximos encontros. Por enquanto, sem cobrança, ideal para conhecer a constelação como observador ou representante."
              whyHeading="Por que participar de uma Constelação Familiar?"
              whyContent={<ParticipateWhySection />}
              steps={stepsParticipate}
              accent="participar"
              promo={{
                label: 'Por tempo limitado',
                detail: 'Acesso gratuito ao grupo de avisos.',
              }}
              cta={{
                label: 'Entrar no grupo de avisos',
                href: WHATSAPP_AVISOS_GRUPO,
              }}
            />
          )}

          {showConstelarJourney && (
            <JourneyCard
              id="constelar"
              eyebrow="Caminho 2"
              title="Fazer a sua constelação"
              intro="Primeiro escolha o constelador e veja os horários disponíveis. Depois, efetue o pagamento para confirmar a sua sessão."
              whyHeading="Por que fazer a sua própria Constelação?"
              whyContent={<ConstellateWhySection />}
              steps={stepsConstellate}
              accent="constelar"
              cta={{ label: 'Escolher constelador', to: '/terapeutas' }}
            />
          )}
        </div>
        )}

        <section
          id="planos"
          className={`plans-animate-in plans-animate-delay-3 scroll-mt-28 ${hasPendingSchedule ? 'mt-4' : 'mt-14'}`}
          aria-label="Constelação e participação"
        >
          {intent !== 'participar' && (
            <div className="mb-10 text-center">
              <p className="mx-auto max-w-3xl text-lg font-bold leading-relaxed text-[#3A5859] sm:text-xl md:text-2xl">
                {hasPendingSchedule ? (
                  <>
                    Escolha o plano abaixo e <span className="text-[#A56096]">pague para confirmar</span> o horário que você já selecionou.
                  </>
                ) : (
                  <>
                    Para <span className="text-[#A56096]">constelar</span>, escolha um constelador, agende o horário e confirme com o pagamento.
                  </>
                )}
              </p>
            </div>
          )}

          {intent === 'participar' && (
            <div className="mb-10 text-center">
              <p className="mx-auto max-w-3xl text-lg font-bold leading-relaxed text-[#3A5859] sm:text-xl md:text-2xl">
                <span className="text-[#3A5859]">Participação gratuita</span> pelo grupo de avisos no WhatsApp.
              </p>
            </div>
          )}

          {offersSideBySide ? (
            <div className="space-y-8">
              <ParticipationPromoBanner className="hidden lg:block" />
              <div className="grid grid-cols-1 items-stretch gap-10 lg:grid-cols-2 lg:gap-8">
                {constellationOffers}
                {participationOffers}
              </div>
            </div>
          ) : (
            <>
              {constellationOffers}
              {participationOffers}
            </>
          )}

          {!isLoading && participationPlans.length === 0 && constellationPlans.length === 0 && (
            <PlansGrid
              plans={[]}
              isLoading={false}
              processingPlan={processingPlan}
              onSelect={onSelect}
              emptyMessage="Nenhum plano disponível no momento."
            />
          )}
        </section>

        <section className="plans-animate-in mt-14 rounded-[1.75rem] border border-[#E8EFEB] bg-white p-6 md:p-8" aria-label="Ajuda">
          <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-center">
            <div>
              <h2 className="text-xl font-bold text-[#3A5859]">Ainda com dúvidas?</h2>
              <p className="mt-2 text-sm text-[#496566]">
                Nossa equipe ajuda você em cada etapa.
              </p>
            </div>
            <div>
              <a
                href="https://api.whatsapp.com/send/?phone=5513997701755&text=Ol%C3%A1%21%20Tenho%20d%C3%BAvidas%20sobre%20participar%20ou%20constelar%20na%20plataforma."
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-[#20BA5A]"
              >
                <MessageCircle className="h-4 w-4" aria-hidden />
                WhatsApp
              </a>
            </div>
          </div>
        </section>

        <section className="plans-animate-in mt-14 mb-4" id="faq" aria-labelledby="participant-faq-heading">
          <h2 id="participant-faq-heading" className="mb-8 text-center text-2xl font-bold text-[#3A5859] sm:text-3xl">
            Perguntas frequentes
          </h2>
          <div className="mx-auto max-w-3xl space-y-4">
            {faqItems.map((item) => (
              <article
                key={item.question}
                className="rounded-[1.5rem] border border-[#E8EFEB] bg-white p-6 shadow-sm"
              >
                <h3 className="text-lg font-bold text-[#3A5859]">{item.question}</h3>
                <p className="mt-3 text-sm leading-relaxed text-[#496566] sm:text-base">{item.answer}</p>
              </article>
            ))}
          </div>
        </section>
      </div>
    </>
  );
};

export default ParticipantPlansLayout;
